import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { useExtracted } from "next-intl";
import { PIN_LENGTH } from "@/lib/constants";

interface PinInputProps {
  onSubmit: (pin: string) => void;
  loading: boolean;
  error: boolean;
  onErrorClear: () => void;
  resetKey: number;
}

export function PinInput({
  onSubmit,
  loading,
  error,
  onErrorClear,
  resetKey,
}: PinInputProps) {
  const te = useExtracted();
  const [digits, setDigits] = useState<string[]>(Array(PIN_LENGTH).fill(""));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setDigits(Array(PIN_LENGTH).fill(""));
    // Defer until after the first paint: motion writes opacity:0 inline
    // for the entrance animation, and Safari (plus some Chrome builds)
    // drops focus calls on still-hidden elements. One rAF lets motion's
    // initial styles commit before we focus.
    const id = requestAnimationFrame(() => {
      inputsRef.current[0]?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [resetKey]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleChange = useCallback(
    (index: number, value: string) => {
      if (!/^[0-9]*$/.test(value)) return;

      const digit = value.slice(-1);
      const next = [...digits];
      next[index] = digit;
      setDigits(next);
      onErrorClear();

      if (digit && index < PIN_LENGTH - 1) {
        inputsRef.current[index + 1]?.focus();
      }

      if (digit && index === PIN_LENGTH - 1) {
        const pin = next.join("");
        if (pin.length === PIN_LENGTH) onSubmit(pin);
      }
    },
    [digits, onSubmit, onErrorClear],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (e.key === "Backspace" && !digits[index] && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    },
    [digits],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData("text")
        .replace(/[^0-9]/g, "")
        .slice(0, PIN_LENGTH);
      if (!pasted) return;

      const next = Array(PIN_LENGTH).fill("");
      for (let i = 0; i < pasted.length; i++) {
        next[i] = pasted[i];
      }
      setDigits(next);

      if (pasted.length === PIN_LENGTH) {
        onSubmit(pasted);
      } else {
        inputsRef.current[pasted.length]?.focus();
      }
    },
    [onSubmit],
  );

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex gap-1.5 sm:gap-2.5">
        {digits.map((digit, i) => (
          <motion.input
            key={i}
            ref={(el) => {
              inputsRef.current[i] = el;
            }}
            animate={{ opacity: 1, y: 0 }}
            autoComplete="one-time-code"
            disabled={loading}
            initial={{ opacity: 0, y: 8 }}
            inputMode="numeric"
            maxLength={1}
            style={{ animationDelay: error ? `${i * 30}ms` : undefined }}
            transition={{
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.08 + i * 0.04,
            }}
            type="text"
            value={digit}
            className={`font-mono-numbers h-13 w-11 rounded-[13px] border bg-[rgba(12,17,20,0.72)] text-center text-lg font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.045),0_8px_20px_rgba(0,0,0,0.16)] backdrop-blur-[10px] transition-[border-color,background-color,box-shadow,opacity] duration-200 outline-none [-webkit-text-security:disc] [text-security:disc] sm:h-14 sm:w-12 ${
              error
                ? "border-loss/70 animate-shake shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_0_0_3px_rgba(255,83,119,0.08),0_8px_20px_rgba(0,0,0,0.18)]"
                : digit
                  ? "border-interactive/55 bg-interactive/[0.055] shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_0_0_3px_rgba(73,222,236,0.055),0_8px_20px_rgba(0,0,0,0.18)]"
                  : "border-white/[0.12] hover:border-white/[0.2] focus:border-interactive/60 focus:bg-interactive/[0.035] focus:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_0_0_3px_rgba(73,222,236,0.06),0_8px_20px_rgba(0,0,0,0.18)]"
            } ${loading ? "opacity-40" : ""}`}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={i === 0 ? handlePaste : undefined}
          />
        ))}
      </div>

      <div className="h-5">
        {error && (
          <p className="label-xs text-loss tracking-[0.15em]">
            {te("INVALID PIN")}
          </p>
        )}
      </div>
    </div>
  );
}
